<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

// Only the icons available in the widget icon set (icons.json)
// so the selected icon will always render correctly in the widget.
const WIDGET_ICONS = [
  'candy-cane',
  'candy',
  'cat',
  'circle-star',
  'circle-user',
  'clock',
  'concierge-bell',
  'cookie',
  'cooking-pot',
  'croissant',
  'dessert',
  'dog',
  'drill',
  'ev-charger',
  'ferris-wheel',
  'fish',
  'flower',
  'forklift',
  'gamepad-2',
  'glass-water',
  'graduation-cap',
  'hamburger',
  'hand-platter',
  'handshake',
  'helicopter',
  'hop',
  'land-plot',
  'life-buoy',
  'lollipop',
  'map',
  'megaphone',
  'mic-vocal',
  'music-4',
  'paintbrush',
  'piano',
  'pizza',
  'popcorn',
  'projector',
  'rabbit',
  'salad',
  'sandwich',
  'save',
  'shield-check',
  'shrimp',
  'signature',
  'soup',
  'sprout',
  'star',
  'tent-tree',
  'ticket-check',
  'trees',
  'umbrella',
  'utensils-crossed',
  'vegan',
  'wallpaper',
  'wine',
  'zap',
  'link',
  'search',
  'video-add',
  'attach',
  'chat',
  'dismiss',
  'document',
  'emoji',
  'home',
  'globe',
  'open',
  'send',
  'users',
  'map-pin',
  'coffee',
  'armchair',
  'at-sign',
  'atom',
  'badge-check',
  'balloon',
  'beef',
  'beer',
  'bell',
  'brush-cleaning',
  'bug',
  'bus',
  'cake',
  'calendar-check',
  'calendar',
  'car-taxi-front',
  'caravan',
  'carrot',
  'cctv',
  'circle-check',
  'citrus',
  'clover',
];

const { t } = useI18n();
const isOpen = ref(false);
const searchTerm = ref('');

const filteredIcons = computed(() => {
  if (!searchTerm.value) return WIDGET_ICONS;
  return WIDGET_ICONS.filter(i => i.includes(searchTerm.value.toLowerCase()));
});

const selectIcon = icon => {
  emit('update:modelValue', icon === props.modelValue ? null : icon);
  isOpen.value = false;
  searchTerm.value = '';
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) searchTerm.value = '';
};
</script>

<template>
  <div class="relative">
    <!-- Backdrop to close on outside click -->
    <div v-if="isOpen" class="fixed inset-0 z-20" @click="isOpen = false" />
    <button
      type="button"
      class="flex items-center justify-center size-10 rounded-md transition-colors shrink-0"
      :class="
        modelValue
          ? 'text-n-brand bg-n-brand/10 hover:bg-n-brand/20'
          : 'text-n-slate-9 hover:text-n-slate-11 hover:bg-n-alpha-2'
      "
      :title="
        modelValue
          ? modelValue
          : t('INBOX_MGMT.PRE_CHAT_FORM.ICON_PICKER.ADD_ICON')
      "
      @click.stop="toggle"
    >
      <FluentIcon v-if="modelValue" :icon="modelValue" size="24" />
      <span v-else class="i-lucide-smile-plus size-6" />
    </button>

    <!-- Dropdown -->
    <div
      v-if="isOpen"
      class="absolute top-9 left-0 z-30 w-80 bg-n-solid-2 rounded-xl outline outline-1 outline-n-weak shadow-lg p-2"
      @click.stop
    >
      <input
        v-model="searchTerm"
        type="text"
        :placeholder="
          t('INBOX_MGMT.PRE_CHAT_FORM.ICON_PICKER.SEARCH_PLACEHOLDER')
        "
        class="reset-base w-full text-sm px-2 py-1.5 mb-2 rounded-lg bg-n-alpha-1 outline outline-1 outline-n-container text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand transition-colors"
        autofocus
      />
      <div class="grid grid-cols-6 gap-1 max-h-36 overflow-y-auto">
        <button
          v-for="icon in filteredIcons"
          :key="icon"
          type="button"
          class="flex items-center justify-center size-10 rounded-md transition-colors"
          :class="
            modelValue === icon
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
          "
          :title="icon"
          @click="selectIcon(icon)"
        >
          <FluentIcon :icon="icon" size="24" />
        </button>
        <p
          v-if="filteredIcons.length === 0"
          class="col-span-6 text-center text-xs text-n-slate-10 py-2"
        >
          {{ t('INBOX_MGMT.PRE_CHAT_FORM.ICON_PICKER.NO_ICONS_FOUND') }}
        </p>
      </div>
    </div>
  </div>
</template>
